import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewIcon from '@mui/icons-material/OpenInNew';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import { type BusinessAssetView } from '../../../utils/assetFilters';
import {
  ASSET_VIEW_ICON_DEFINITIONS,
  DEFAULT_ASSET_VIEW_ICON,
  renderAssetViewIcon,
} from '../../../constants/assetViewIcons';

interface AssetType {
  asset_type_id: string;
  type_name: string;
}
interface Location {
  locationId: string;
  locationName: string;
}
interface JobTag {
  job_tag_id: string;
  tag_name: string;
  tag_scope?: string;
}

const EMPTY_VIEW: Omit<BusinessAssetView, 'id'> = {
  label: '',
  sortOrder: 0,
  showInWeb: true,
  showInMobile: true,
  icon: DEFAULT_ASSET_VIEW_ICON,
  filters: {},
};

const AssetViewsSettings: React.FC = () => {
  const [views, setViews] = useState<BusinessAssetView[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingView, setEditingView] = useState<BusinessAssetView | null>(null);
  const [form, setForm] = useState<Omit<BusinessAssetView, 'id'>>(EMPTY_VIEW);

  // Delete confirmation
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Reference data for filter builder
  const [assetTypes, setAssetTypes] = useState<AssetType[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [jobTags, setJobTags] = useState<JobTag[]>([]);
  const [refLoaded, setRefLoaded] = useState(false);

  const businessId = localStorage.getItem('businessId');

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  useEffect(() => {
    if (!businessId) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const [viewsRes, typesRes, locsRes, tagsRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/businesses/${businessId}/asset-views`, { headers: authHeaders() }),
          axios.get(`${API_BASE_URL}/asset-types`, { headers: authHeaders() }),
          axios.get(`${API_BASE_URL}/locations`, { headers: authHeaders() }),
          axios.get(`${API_BASE_URL}/job-tags`, { headers: authHeaders() }),
        ]);
        if (!cancelled) {
          setViews(Array.isArray(viewsRes.data.asset_views) ? viewsRes.data.asset_views : []);
          setAssetTypes(Array.isArray(typesRes.data) ? typesRes.data : (typesRes.data?.data ?? []));
          setLocations(Array.isArray(locsRes.data) ? locsRes.data : (locsRes.data?.locations ?? locsRes.data?.data ?? []));
          const rawTags = Array.isArray(tagsRes.data) ? tagsRes.data : (tagsRes.data?.tags ?? tagsRes.data?.data ?? []);
          setJobTags(rawTags.filter((t: JobTag) => t.tag_scope !== 'user'));
          setRefLoaded(true);
        }
      } catch (e: any) {
        if (!cancelled) setError(e.response?.data?.message ?? 'Failed to load asset views');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  const saveViews = async (updated: BusinessAssetView[]) => {
    setSaving(true);
    setError(null);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/businesses/${businessId}/asset-views`,
        { asset_views: updated },
        { headers: authHeaders() }
      );
      const saved: BusinessAssetView[] = res.data.asset_views ?? updated;
      setViews(saved);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      // Notify AdminLayout to reload business data for sidebar refresh
      window.dispatchEvent(new Event('assetViewsChanged'));
      return saved;
    } catch (e: any) {
      setError(e.response?.data?.message ?? 'Failed to save asset views');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const openAdd = () => {
    setEditingView(null);
    setForm({ ...EMPTY_VIEW, sortOrder: views.length });
    setDialogOpen(true);
  };

  const openEdit = (view: BusinessAssetView) => {
    setEditingView(view);
    setForm({
      label: view.label,
      sortOrder: view.sortOrder,
      showInWeb: view.showInWeb,
      showInMobile: view.showInMobile,
      icon: view.icon ?? DEFAULT_ASSET_VIEW_ICON,
      filters: { ...view.filters },
    });
    setDialogOpen(true);
  };

  const handleSaveDialog = async () => {
    if (!form.label.trim()) return;
    let updated: BusinessAssetView[];
    if (editingView) {
      updated = views.map(v => v.id === editingView.id ? { ...editingView, ...form } : v);
    } else {
      updated = [...views, { id: crypto.randomUUID(), ...form }];
    }
    const saved = await saveViews(updated);
    if (saved !== null) setDialogOpen(false);
  };

  const handleDelete = async (id: string) => {
    const updated = views.filter(v => v.id !== id);
    await saveViews(updated);
    setDeleteId(null);
  };

  const setFilter = (key: string, value: string | string[] | undefined) => {
    setForm(prev => ({
      ...prev,
      filters: { ...prev.filters, [key]: value },
    }));
  };

  const f = form.filters;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>Asset Views</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Create custom named views (e.g. "Stock") that appear as sidebar shortcuts in the web admin and
        quick-access buttons in the mobile app. Each view saves a set of asset filters that is applied
        automatically when clicked.
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Asset views saved.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} disabled={saving}>
          Add View
        </Button>
      </Box>

      {views.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
          <Typography variant="body2" color="text.secondary">
            No custom views yet. Add one to create a bespoke sidebar shortcut.
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none', overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={48} />
                <TableCell>Label</TableCell>
                <TableCell>Filters</TableCell>
                <TableCell align="center">Web</TableCell>
                <TableCell align="center">Mobile</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {views.map(view => {
                const filterSummary: string[] = [];
                if (view.filters.status && view.filters.status !== 'all') filterSummary.push(`Status: ${view.filters.status}`);
                if (view.filters.loanStatus && view.filters.loanStatus !== 'all') filterSummary.push(`Loan: ${view.filters.loanStatus.replace('_', ' ')}`);
                if (view.filters.outOfAction && view.filters.outOfAction !== 'all') filterSummary.push(`OOA: ${view.filters.outOfAction === 'true' ? 'yes' : 'no'}`);
                if (view.filters.assetType && view.filters.assetType !== 'all') {
                  const t = assetTypes.find(at => at.asset_type_id === view.filters.assetType);
                  if (t) filterSummary.push(`Type: ${t.type_name}`);
                }
                if (view.filters.jobTag && view.filters.jobTag !== 'all') {
                  const tag = jobTags.find(t => t.job_tag_id === view.filters.jobTag);
                  filterSummary.push(view.filters.jobTag === 'my_tags' ? 'Tag: my roles' : `Tag: ${tag?.tag_name ?? view.filters.jobTag}`);
                }
                if (view.filters.excludeJobTagIds?.length) {
                  filterSummary.push(`Excl. ${view.filters.excludeJobTagIds.length} tag(s)`);
                }
                return (
                  <TableRow key={view.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
                        {renderAssetViewIcon(view.icon, 'small')}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{view.label}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {filterSummary.length === 0 ? (
                          <Typography variant="caption" color="text.disabled">None</Typography>
                        ) : filterSummary.map(s => (
                          <Chip key={s} label={s} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {view.showInWeb ? '✓' : '–'}
                    </TableCell>
                    <TableCell align="center">
                      {view.showInMobile ? '✓' : '–'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Preview in assets list">
                        <IconButton size="small" onClick={() => window.open(`/admin/asset-views/${view.id}`, '_blank')}>
                          <PreviewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => openEdit(view)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => setDeleteId(view.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Add / Edit dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingView ? 'Edit View' : 'Add Asset View'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, overflow: 'visible' }}>
          <TextField
            label="View label"
            value={form.label}
            onChange={e => setForm(p => ({ ...p, label: e.target.value.slice(0, 40) }))}
            required
            fullWidth
            margin="normal"
            helperText="Shown in the sidebar and mobile app (max 40 chars)"
            inputProps={{ maxLength: 40 }}
            autoFocus
            sx={{
              mt: 0.5,
              '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                bgcolor: 'background.paper',
                px: 0.5,
              },
            }}
          />

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Icon</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Shown in the web sidebar and mobile app shortcut
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(72px, 1fr))',
                gap: 1,
              }}
            >
              {ASSET_VIEW_ICON_DEFINITIONS.map(({ key, label }) => {
                const selected = (form.icon ?? DEFAULT_ASSET_VIEW_ICON) === key;
                return (
                  <Tooltip key={key} title={label}>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setForm(p => ({ ...p, icon: key }))}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        p: 1,
                        border: '1px solid',
                        borderColor: selected ? 'primary.main' : 'divider',
                        borderRadius: 1.5,
                        bgcolor: selected ? 'action.selected' : 'background.paper',
                        color: selected ? 'primary.main' : 'text.secondary',
                        cursor: 'pointer',
                        minHeight: 64,
                        '&:hover': {
                          borderColor: 'primary.main',
                          bgcolor: selected ? 'action.selected' : 'action.hover',
                        },
                      }}
                    >
                      {renderAssetViewIcon(key, 'small')}
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', lineHeight: 1.2, textAlign: 'center' }}>
                        {label}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControlLabel
              control={<Switch checked={form.showInWeb} onChange={e => setForm(p => ({ ...p, showInWeb: e.target.checked }))} />}
              label="Show in web sidebar"
            />
            <FormControlLabel
              control={<Switch checked={form.showInMobile} onChange={e => setForm(p => ({ ...p, showInMobile: e.target.checked }))} />}
              label="Show in mobile app"
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mt: 1 }}>Filters</Typography>

          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select
              value={f.status ?? 'all'}
              label="Status"
              onChange={e => setFilter('status', e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Loan Status</InputLabel>
            <Select
              value={f.loanStatus ?? 'all'}
              label="Loan Status"
              onChange={e => setFilter('loanStatus', e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="available">Available</MenuItem>
              <MenuItem value="loaned_out">On Loan</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Out of Action</InputLabel>
            <Select
              value={f.outOfAction ?? 'all'}
              label="Out of Action"
              onChange={e => setFilter('outOfAction', e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="false">In action (available)</MenuItem>
              <MenuItem value="true">Out of action</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth size="small">
            <InputLabel>Lock Status</InputLabel>
            <Select
              value={f.lockStatus ?? 'all'}
              label="Lock Status"
              onChange={e => setFilter('lockStatus', e.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="unlocked">Not in use</MenuItem>
              <MenuItem value="locked">In use (locked)</MenuItem>
            </Select>
          </FormControl>

          {refLoaded && assetTypes.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Asset Type</InputLabel>
              <Select
                value={f.assetType ?? 'all'}
                label="Asset Type"
                onChange={e => setFilter('assetType', e.target.value)}
              >
                <MenuItem value="all">All types</MenuItem>
                {assetTypes.map(t => (
                  <MenuItem key={t.asset_type_id} value={t.asset_type_id}>{t.type_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {refLoaded && locations.length > 0 && (
            <FormControl fullWidth size="small">
              <InputLabel>Home Location</InputLabel>
              <Select
                value={f.location ?? 'all'}
                label="Home Location"
                onChange={e => setFilter('location', e.target.value)}
              >
                <MenuItem value="all">All locations</MenuItem>
                {locations.map(l => (
                  <MenuItem key={l.locationId} value={l.locationId}>{l.locationName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {refLoaded && jobTags.length > 0 && (
            <>
              <FormControl fullWidth size="small">
                <InputLabel>Include Tag</InputLabel>
                <Select
                  value={f.jobTag ?? 'all'}
                  label="Include Tag"
                  onChange={e => setFilter('jobTag', e.target.value)}
                >
                  <MenuItem value="all">Any tag</MenuItem>
                  <MenuItem value="my_tags">My job role tags</MenuItem>
                  {jobTags.map(t => (
                    <MenuItem key={t.job_tag_id} value={t.job_tag_id}>{t.tag_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Exclude Tags</InputLabel>
                <Select
                  multiple
                  value={f.excludeJobTagIds ?? []}
                  label="Exclude Tags"
                  onChange={e => setFilter('excludeJobTagIds', e.target.value as string[])}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(selected as string[]).map(id => {
                        const tag = jobTags.find(t => t.job_tag_id === id);
                        return tag ? <Chip key={id} label={tag.tag_name} size="small" /> : null;
                      })}
                    </Box>
                  )}
                >
                  {jobTags.map(t => (
                    <MenuItem key={t.job_tag_id} value={t.job_tag_id}>{t.tag_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
                Assets with ANY of the excluded tags will be hidden from this view.
              </Typography>
            </>
          )}

          <TextField
            label="Default search query (optional)"
            value={f.searchQuery ?? ''}
            onChange={e => setFilter('searchQuery', e.target.value)}
            size="small"
            helperText="Pre-fill the search box when this view is opened"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveDialog}
            disabled={!form.label.trim() || saving}
          >
            {saving ? <CircularProgress size={18} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)}>
        <DialogTitle>Delete view?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "
            {views.find(v => v.id === deleteId)?.label}"? This will remove it from the sidebar and
            mobile app.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deleteId && handleDelete(deleteId)}
            disabled={saving}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetViewsSettings;
